import { neonClient } from '../lib/neon';
import { r2Storage } from '../lib/neonStorage';

const WORKER_URL = import.meta.env.VITE_WORKER_URL; // Optional Worker Proxy
const WORKER_SECRET = import.meta.env.VITE_WORKER_SECRET;

/**
 * LaSea Inventory Service
 * Migrated to Neon (Data API) + Cloudflare R2
 */

// ==================== INVENTORY CRUD OPERATIONS ====================

/**
 * Create a new inventory item
 * @param {Object} inventoryData - Inventory item data (camelCase)
 * @returns {Object} Created inventory item with ID
 */
export const createInventoryItem = async (inventoryData) => {
  try {
    // Convert camelCase to snake_case for database
    const dbData = {
      client_id: inventoryData?.clientId,
      item_name: inventoryData?.itemName,
      description: inventoryData?.description || null,
      category: inventoryData?.category || null,
      condition: inventoryData?.condition || 'good',
      delivery_status: inventoryData?.deliveryStatus || 'in_warehouse',
      purchase_price: inventoryData?.purchasePrice || null,
      current_value: inventoryData?.currentValue || null,
      location: inventoryData?.location || null,
      notes: inventoryData?.notes || null,
      is_active: inventoryData?.isActive !== undefined ? inventoryData?.isActive : true
    };

    const { data, error } = await neonClient?.from('lasea_inventory')?.insert([dbData]);

    if (error) {
      console.error('Supabase error:', error);
      throw new Error(error.message || 'Failed to create inventory item');
    }

    if (!data) {
      throw new Error('No data returned from database');
    }

    // data is an array from INSERT RETURNING * — grab the first row
    const row = Array.isArray(data) ? data[0] : data;
    if (!row?.id) throw new Error('Failed to create inventory item — no ID returned');

    // Convert snake_case back to camelCase
    return convertInventoryToCamelCase(row);
  } catch (error) {
    console.error('Error creating inventory item:', error);

    // Handle different error types
    if (error?.message?.includes('Network request failed') ||
      error?.message?.includes('Load failed') ||
      error?.message?.includes('Failed to fetch')) {
      throw new Error('Unable to connect to database. Please check your internet connection, refresh your browser, and try again.');
    }

    if (error?.message?.includes('JWT') || error?.message?.includes('auth')) {
      throw new Error('Authentication error. Please log out and log back in.');
    }

    throw error;
  }
};

/**
 * Get all inventory items for a client
 * @param {string} clientId - UUID of the client
 * @param {Object} filters - Optional filters (category, condition, isActive)
 * @returns {Array} List of inventory items
 */
export const getClientInventory = async (clientId, filters = {}) => {
  try {
    console.log('getClientInventory called with:', { clientId, filters });

    let query = neonClient?.from('lasea_inventory')?.select(`
        *,
        photos:lasea_inventory_photos(
          id,
          photo_path,
          display_order,
          is_primary
        )
      `)?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.condition) {
      query = query?.eq('condition', filters?.condition);
    }
    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }

    const { data, error } = await query;

    console.log('getClientInventory raw response:', { data, error });

    if (error) throw error;

    // Convert snake_case to camelCase with photos and UI-compatible fields
    const converted = await Promise.all(data?.map(async (item) => {
      const photos = item?.photos || [];
      const primaryPhoto = photos?.find(p => p?.is_primary) || photos?.[0];

      // Generate signed URL for primary photo if it exists
      let primaryImageUrl = '/assets/images/no_image.png';
      if (primaryPhoto?.photo_path) {
        const { data: signedUrlData } = await r2Storage
          ?.from('lasea-inventory-photos')
          ?.createSignedUrl(primaryPhoto?.photo_path, 3600); // 1 hour expiry

        if (signedUrlData?.signedUrl) {
          primaryImageUrl = signedUrlData?.signedUrl;
        }
      }

      // Generate signed URLs for all photos
      const imagesWithUrls = await Promise.all(photos?.map(async (p) => {
        let photoUrl = '/assets/images/no_image.png';
        if (p?.photo_path) {
          const { data: signedUrlData } = await r2Storage
            ?.from('lasea-inventory-photos')
            ?.createSignedUrl(p?.photo_path, 3600);

          if (signedUrlData?.signedUrl) {
            photoUrl = signedUrlData?.signedUrl;
          }
        }

        return {
          id: p?.id,
          url: photoUrl,
          alt: `${item?.item_name} - image ${p?.display_order + 1}`,
          isPrimary: p?.is_primary
        };
      }));

      return {
        ...convertInventoryToCamelCase(item),
        // Add UI-compatible field mappings
        name: item?.item_name,
        liin: `LIIN-${String(item?.id)?.slice(0, 8)?.toUpperCase()}`,
        status: item?.condition,
        deliveryStatus: item?.delivery_status,
        arrivalDate: item?.created_at,
        primaryImage: primaryImageUrl,
        primaryImageAlt: `${item?.item_name || 'Inventory item'} - primary image`,
        imageCount: photos?.length || 0,
        images: imagesWithUrls
      };
    }));
    console.log('getClientInventory converted data:', converted);

    return converted;
  } catch (error) {
    console.error('Error fetching client inventory:', error);
    throw error;
  }
};

/**
 * Get all inventory items (admin view)
 * @param {Object} filters - Optional filters
 * @returns {Array} List of all inventory items with client info
 */
export const getAllInventory = async (filters = {}) => {
  try {
    let query = neonClient?.from('lasea_inventory')?.select(`
        *,
        client:lasea_users!client_id (
          id,
          full_name,
          email,
          company_name
        ),
        photos:lasea_inventory_photos(
          id,
          photo_path,
          display_order,
          is_primary
        )
      `)?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.condition) {
      query = query?.eq('condition', filters?.condition);
    }
    if (filters?.isActive !== undefined) {
      query = query?.eq('is_active', filters?.isActive);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase with client info and photos
    const converted = await Promise.all(data?.map(async (item) => {
      const photos = item?.photos || [];
      const primaryPhoto = photos?.find(p => p?.is_primary) || photos?.[0];

      // Generate signed URL for primary photo if it exists
      let primaryImageUrl = '/assets/images/no_image.png';
      if (primaryPhoto?.photo_path) {
        const { data: signedUrlData } = await r2Storage
          ?.from('lasea-inventory-photos')
          ?.createSignedUrl(primaryPhoto?.photo_path, 3600); // 1 hour expiry

        if (signedUrlData?.signedUrl) {
          primaryImageUrl = signedUrlData?.signedUrl;
        }
      }

      // Generate signed URLs for all photos
      const imagesWithUrls = await Promise.all(photos?.map(async (p) => {
        let photoUrl = '/assets/images/no_image.png';
        if (p?.photo_path) {
          const { data: signedUrlData } = await r2Storage
            ?.from('lasea-inventory-photos')
            ?.createSignedUrl(p?.photo_path, 3600);

          if (signedUrlData?.signedUrl) {
            photoUrl = signedUrlData?.signedUrl;
          }
        }

        return {
          id: p?.id,
          url: photoUrl,
          alt: `${item?.item_name} - image ${p?.display_order + 1}`,
          isPrimary: p?.is_primary
        };
      }));

      return {
        ...convertInventoryToCamelCase(item),
        name: item?.item_name,
        liin: `LIIN-${String(item?.id)?.slice(0, 8)?.toUpperCase()}`,
        status: item?.condition,
        deliveryStatus: item?.delivery_status,
        arrivalDate: item?.created_at,
        primaryImage: primaryImageUrl,
        primaryImageAlt: `${item?.item_name || 'Inventory item'} - primary image`,
        imageCount: photos?.length || 0,
        images: imagesWithUrls,
        client: item?.client ? {
          id: item?.client?.id,
          fullName: item?.client?.full_name,
          email: item?.client?.email,
          companyName: item?.client?.company_name
        } : null
      };
    }));

    return converted;
  } catch (error) {
    console.error('Error fetching all inventory:', error);
    throw error;
  }
};

/**
 * Get a single inventory item by ID
 * @param {string} inventoryId - UUID of the inventory item
 * @returns {Object} Inventory item details
 */
export const getInventoryItem = async (inventoryId) => {
  try {
    let query = neonClient?.from('lasea_inventory')?.select('*');

    // Check if the input is a LIIN (starts with "LIIN-") or a UUID
    if (inventoryId?.toString()?.startsWith('LIIN-')) {
      // Extract the UUID prefix from LIIN (e.g., "LIIN-0ACF0678" -> "0acf0678")
      const liinPrefix = inventoryId?.substring(5)?.toLowerCase(); // Remove "LIIN-" and lowercase

      // Query all items and filter by UUID prefix match
      const { data, error } = await query;

      if (error) throw error;

      // Find the item where UUID starts with this prefix
      const item = data?.find(item => item?.id?.toLowerCase()?.startsWith(liinPrefix));

      if (!item) {
        throw new Error(`No item found with LIIN: ${inventoryId}`);
      }

      return convertInventoryToCamelCase(item);
    } else {
      // Query by ID field (UUID)
      const { data, error } = await query?.eq('id', inventoryId)?.single();

      if (error) throw error;

      return convertInventoryToCamelCase(data);
    }
  } catch (error) {
    console.error('Error fetching inventory item:', error);
    throw error;
  }
};

/**
 * Update an inventory item
 * @param {string} inventoryId - UUID of the inventory item
 * @param {Object} updates - Fields to update (camelCase)
 * @returns {Object} Updated inventory item
 */
export const updateInventoryItem = async (inventoryId, updates) => {
  try {
    // Convert camelCase to snake_case for database
    const dbUpdates = {};

    if (updates?.itemName !== undefined) dbUpdates.item_name = updates?.itemName;
    if (updates?.description !== undefined) dbUpdates.description = updates?.description;
    if (updates?.category !== undefined) dbUpdates.category = updates?.category;
    if (updates?.condition !== undefined) dbUpdates.condition = updates?.condition;
    if (updates?.deliveryStatus !== undefined) dbUpdates.delivery_status = updates?.deliveryStatus;
    if (updates?.purchasePrice !== undefined) dbUpdates.purchase_price = updates?.purchasePrice;
    if (updates?.currentValue !== undefined) dbUpdates.current_value = updates?.currentValue;
    if (updates?.location !== undefined) dbUpdates.location = updates?.location;
    if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;
    if (updates?.isActive !== undefined) dbUpdates.is_active = updates?.isActive;

    console.log('🔄 updateInventoryItem - Received updates (camelCase):', JSON.stringify(updates, null, 2));
    console.log('🔄 updateInventoryItem - DB updates (snake_case):', JSON.stringify(dbUpdates, null, 2));

    const { data, error } = await neonClient?.from('lasea_inventory')?.update(dbUpdates)?.eq('id', inventoryId);

    if (error) {
      console.error('❌ updateInventoryItem - Supabase error:', error);
      throw error;
    }

    console.log('✅ updateInventoryItem - Supabase response:', data);
    console.log('✅ updateInventoryItem - current_value in response:', data?.current_value);

    return convertInventoryToCamelCase(data);
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw error;
  }
};

/**
 * Delete an inventory item
 * @param {string} inventoryId - UUID of the inventory item
 * @returns {boolean} Success status
 */
export const deleteInventoryItem = async (inventoryId) => {
  try {
    const { error } = await neonClient?.from('lasea_inventory')?.delete()?.eq('id', inventoryId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting inventory item:', error);
    throw error;
  }
};

// ==================== INVENTORY PHOTOS OPERATIONS ====================

/**
 * Upload inventory photo to storage
 * @param {File} file - Image file to upload
 * @param {string} inventoryId - UUID of the inventory item
 * @returns {string} Storage path of uploaded file
 */
export const uploadInventoryPhoto = async (file, inventoryId) => {
  try {
    const fileExt = file?.name?.split('.')?.pop() || 'jpg';
    const fileName = `${Date.now()}.${fileExt}`;
    // R2 key — stored in DB and served via /read/<key>
    const key = `${inventoryId}/${fileName}`;

    if (WORKER_URL) {
      const response = await fetch(`${WORKER_URL}/upload/${key}`, {
        method: 'POST',
        headers: { 'X-Worker-Auth': WORKER_SECRET },
        body: file
      });

      if (!response.ok) throw new Error('Worker upload failed');
      const result = await response.json();
      // result.path is the R2 key returned by the worker
      return result.path || key;
    } else {
      // Direct R2 Upload (using S3 SDK)
      const { data, error } = await r2Storage.from('lasea-inventory-photos').upload(key, file);
      if (error) throw error;
      return data.path;
    }
  } catch (error) {
    console.error('Error uploading inventory photo:', error);
    throw error;
  }
};

/**
 * Add photo record to inventory
 * @param {string} inventoryId - UUID of the inventory item
 * @param {string} photoPath - Storage path of the photo
 * @param {boolean} isPrimary - Whether this is the primary photo
 * @param {number} displayOrder - Display order (optional)
 * @returns {Object} Created photo record
 */
export const addInventoryPhotoRecord = async (inventoryId, photoPath, isPrimary = false, displayOrder = 0) => {
  try {
    const { data, error } = await neonClient?.from('lasea_inventory_photos')?.insert([{
      inventory_id: inventoryId,
      photo_path: photoPath,
      is_primary: isPrimary,
      display_order: displayOrder
    }]);

    if (error) throw error;

    return convertPhotoToCamelCase(data);
  } catch (error) {
    console.error('Error adding inventory photo record:', error);
    throw error;
  }
};

/**
 * Get all photos for an inventory item
 * @param {string} inventoryId - UUID of the inventory item
 * @returns {Array} List of photo records with signed URLs
 */
export const getInventoryPhotos = async (inventoryId) => {
  try {
    const { data, error } = await neonClient?.from('lasea_inventory_photos')?.select('*')?.eq('inventory_id', inventoryId)?.order('display_order', { ascending: true });

    if (error) throw error;

    // Get signed URLs for each photo
    const photosWithUrls = await Promise.all(
      data?.map(async (photo) => {
        const { data: urlData } = await r2Storage?.from('lasea-inventory-photos')?.createSignedUrl(photo?.photo_path, 3600); // 1 hour expiry

        return {
          ...convertPhotoToCamelCase(photo),
          signedUrl: urlData?.signedUrl || null
        };
      })
    );

    return photosWithUrls;
  } catch (error) {
    console.error('Error fetching inventory photos:', error);
    throw error;
  }
};

/**
 * Delete inventory photo
 * @param {string} photoId - UUID of the photo record
 * @param {string} photoPath - Storage path of the photo
 * @returns {boolean} Success status
 */
export const deleteInventoryPhoto = async (photoId, photoPath) => {
  try {
    // Delete from storage
    const { error: storageError } = await r2Storage?.from('lasea-inventory-photos')?.remove([photoPath]);

    if (storageError) throw storageError;

    // Delete database record
    const { error: dbError } = await neonClient?.from('lasea_inventory_photos')?.delete()?.eq('id', photoId);

    if (dbError) throw dbError;

    return true;
  } catch (error) {
    console.error('Error deleting inventory photo:', error);
    throw error;
  }
};

/**
 * Set primary photo for inventory item
 * @param {string} inventoryId - UUID of the inventory item
 * @param {string} photoId - UUID of the photo to set as primary
 * @returns {boolean} Success status
 */
export const setPrimaryPhoto = async (inventoryId, photoId) => {
  try {
    // First, unset all primary photos for this inventory item
    await neonClient?.from('lasea_inventory_photos')?.update({ is_primary: false })?.eq('inventory_id', inventoryId);

    // Then set the specified photo as primary
    const { error } = await neonClient?.from('lasea_inventory_photos')?.update({ is_primary: true })?.eq('id', photoId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error setting primary photo:', error);
    throw error;
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Convert inventory data from snake_case to camelCase
 * @param {Object} data - Database data in snake_case
 * @returns {Object} Converted data in camelCase
 */
const convertInventoryToCamelCase = (data) => {
  return {
    id: data?.id,
    clientId: data?.client_id,
    itemName: data?.item_name,
    description: data?.description,
    category: data?.category,
    condition: data?.condition,
    deliveryStatus: data?.delivery_status,
    purchasePrice: data?.purchase_price,
    currentValue: data?.current_value,
    location: data?.location,
    notes: data?.notes,
    isActive: data?.is_active,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  };
};

/**
 * Convert photo data from snake_case to camelCase
 * @param {Object} data - Database data in snake_case
 * @returns {Object} Converted data in camelCase
 */
const convertPhotoToCamelCase = (data) => {
  return {
    id: data?.id,
    inventoryId: data?.inventory_id,
    photoPath: data?.photo_path,
    displayOrder: data?.display_order,
    isPrimary: data?.is_primary,
    createdAt: data?.created_at
  };
};

/**
 * Get inventory statistics for a client
 * @param {string} clientId - UUID of the client
 * @returns {Object} Inventory statistics
 */
export const getInventoryStats = async (clientId) => {
  try {
    const { data, error } = await neonClient?.from('lasea_inventory')?.select('*')?.eq('client_id', clientId);

    if (error) throw error;

    return {
      totalItems: data?.length || 0,
      itemsInRoute: data?.filter(item => item?.delivery_status === 'in_route')?.length || 0,
      totalInventoryValue: data?.reduce((sum, item) => sum + (parseFloat(item?.current_value) || 0), 0)
    };
  } catch (error) {
    console.error('Error fetching inventory stats:', error);
    throw error;
  }
};